import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { LineChart as LChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceArea, ReferenceLine, Label } from "recharts";
import { Plus, Trash2, Target, Ruler, Clock, ChevronRight, Sparkles } from "lucide-react";

const MEASUREMENT_FIELDS = [
  { key: "cintura_cm", label: "Cintura", hint: "Mide alrededor de la parte más estrecha de la cintura, sin apretar la cinta." },
  { key: "abdomen_cm", label: "Abdomen", hint: "Mide por encima del ombligo." },
  { key: "cadera_cm", label: "Cadera", hint: "Mide aproximadamente 3–4 dedos por debajo del ombligo." },
  { key: "gluteo_cm", label: "Glúteo", hint: "Mide alrededor de la parte más sobresaliente de los glúteos." },
  { key: "muslo_cm", label: "Muslo", hint: "Mide la parte alta del muslo, pegado al aductor." },
  { key: "cuadriceps_cm", label: "Cuádriceps", hint: "Mide aproximadamente 4 dedos por encima de la rodilla." },
  { key: "pecho_cm", label: "Pecho", hint: "Mide alrededor del torso a la altura de los pezones.", onlyFor: "hombre" },
  { key: "brazo_cm", label: "Brazo", hint: "Mide la parte más ancha del bíceps, siempre en la misma posición.", onlyFor: "hombre" },
  { key: "pantorrilla_cm", label: "Pantorrilla", hint: "Mide la parte más ancha de la pantorrilla.", onlyFor: "hombre" },
  { key: "hombros_cm", label: "Hombros", hint: "Mide la circunferencia alrededor de hombros y parte superior del torso.", onlyFor: "hombre" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function Progress() {
  const [weights, setWeights] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [profile, setProfile] = useState(null);
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // Measurements form state (all optional — include every possible field; UI shows only the ones relevant to the user's sex)
  const [m, setM] = useState({
    cintura_cm: "", abdomen_cm: "", cadera_cm: "", gluteo_cm: "",
    muslo_cm: "", cuadriceps_cm: "",
    pecho_cm: "", brazo_cm: "", pantorrilla_cm: "", hombros_cm: "",
  });
  const [mNote, setMNote] = useState("");
  const [mLoading, setMLoading] = useState(false);

  // Which measurement is shown in the evolution chart (tab selector)
  const [chartField, setChartField] = useState("cintura_cm");

  const fetchAll = async () => {
    const [w, p, meas] = await Promise.all([
      api.get("/weights"),
      api.get("/profile"),
      api.get("/measurements"),
    ]);
    setWeights(w.data);
    setProfile(p.data);
    setMeasurements(meas.data);
  };
  useEffect(() => {
    fetchAll();
    // Auto-scroll to #medidas if hash present
    if (window.location.hash === "#medidas") {
      setTimeout(() => document.getElementById("medidas")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!val) return;
    setLoading(true);
    try {
      await api.post("/weights", { weight_kg: parseFloat(val), note: note || null });
      setVal(""); setNote("");
      await fetchAll();
      toast.success("Peso registrado (no modifica tus medidas corporales)");
    } catch {
      toast.error("Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  const submitMeasurements = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const f of MEASUREMENT_FIELDS) {
      const v = m[f.key];
      if (v !== "" && v !== null && v !== undefined) payload[f.key] = parseFloat(v);
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Indica al menos una medida.");
      return;
    }
    if (mNote) payload.note = mNote;
    setMLoading(true);
    try {
      await api.post("/measurements", payload);
      setM({
        cintura_cm: "", abdomen_cm: "", cadera_cm: "", gluteo_cm: "",
        muslo_cm: "", cuadriceps_cm: "",
        pecho_cm: "", brazo_cm: "", pantorrilla_cm: "", hombros_cm: "",
      });
      setMNote("");
      await fetchAll();
      toast.success("Medidas registradas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar medidas");
    } finally {
      setMLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/weights/${id}`);
      await fetchAll();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const removeMeasurement = async (id) => {
    try {
      await api.delete(`/measurements/${id}`);
      await fetchAll();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const chartData = weights.map((w) => ({
    date: new Date(w.recorded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
    peso: w.weight_kg,
  }));

  // Fitness zone band based on profile range
  const rmin = profile?.range_min_kg || 0;
  const rmax = profile?.range_max_kg || 0;
  const hasRange = rmin > 0 && rmax > 0;

  const yDomain = (() => {
    if (chartData.length === 0 && !hasRange) return ["auto", "auto"];
    const weightsArr = chartData.map((d) => d.peso);
    const lo = Math.min(...weightsArr, hasRange ? rmin : Infinity);
    const hi = Math.max(...weightsArr, hasRange ? rmax : -Infinity);
    if (!isFinite(lo) || !isFinite(hi)) return ["auto", "auto"];
    const pad = Math.max(2, (hi - lo) * 0.15);
    return [Math.floor(lo - pad), Math.ceil(hi + pad)];
  })();

  const zoneStatus = (() => {
    if (!hasRange || weights.length === 0) return null;
    const last = weights[weights.length - 1].weight_kg;
    if (last >= rmin - 0.1 && last <= rmax + 0.1) {
      return { kind: "in", text: "Estás dentro de tu zona fitness recomendada." };
    }
    if (last > rmax) {
      return { kind: "above", text: "Meta inicial sugerida: avanzar poco a poco hacia tu rango." };
    }
    return { kind: "below", text: "Prioriza energía, fuerza y bienestar antes que seguir bajando." };
  })();

  const message = (() => {
    if (!profile || weights.length === 0) return "Registra tu primer peso para empezar tu seguimiento.";
    const first = weights[0].weight_kg;
    const last = weights[weights.length - 1].weight_kg;
    const goal = profile.goal;
    const change = last - first;
    if (goal === "adelgazar") {
      if (change < -0.3) return `¡Increíble! Has bajado ${Math.abs(change).toFixed(1)} kg. Sigue así.`;
      if (change > 0.3) return "Cada inicio cuesta. Mantén la disciplina con tus comidas y rutina.";
      return "Vas estable. Pequeños ajustes hoy hacen grandes cambios mañana.";
    }
    if (goal === "ganar_masa") {
      if (change > 0.3) return `¡Has subido ${change.toFixed(1)} kg! Tu trabajo está dando frutos.`;
      return "Asegúrate de comer suficiente proteína y entrenar con progresión.";
    }
    return "Mantener peso requiere consistencia. Vas bien.";
  })();

  // --- Body measurement computed data ---
  // Fields relevant to the current user based on sex. We NEVER drop data: entries saved
  // when the sex was different will still appear in history/chart, but the form, targets
  // and comparativa only expose what matches the active profile.sex.
  const userSex = profile?.sex || null;
  const visibleFields = MEASUREMENT_FIELDS.filter(
    (f) => !f.onlyFor || f.onlyFor === userSex
  );

  // Build "last known per field" with their REAL last update date.
  const lastKnown = {};
  const lastDate = {};
  const prevKnown = {};
  for (const entry of measurements) {
    for (const f of MEASUREMENT_FIELDS) {
      const v = entry[f.key];
      if (v !== null && v !== undefined) {
        if (lastKnown[f.key] !== undefined) prevKnown[f.key] = lastKnown[f.key];
        lastKnown[f.key] = v;
        lastDate[f.key] = entry.recorded_at;
      }
    }
  }
  const lastMeasurementDate = measurements.length ? measurements[measurements.length - 1].recorded_at : null;
  const daysSince = lastMeasurementDate
    ? Math.max(0, Math.floor((Date.now() - new Date(lastMeasurementDate).getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  const dueForMeasurements = daysSince === null || daysSince >= 30;

  // Chart data per measurement — only entries where that field was non-null (no duplicates).
  const measurementChart = (() => {
    if (measurements.length === 0) return [];
    return measurements.map((entry) => {
      const row = {
        date: new Date(entry.recorded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
      };
      for (const f of MEASUREMENT_FIELDS) {
        if (entry[f.key] !== null && entry[f.key] !== undefined) {
          row[f.key] = entry[f.key];
        }
      }
      return row;
    });
  })();

  // Count explicit entries per measurement field (null-skipping).
  const explicitCount = {};
  for (const f of MEASUREMENT_FIELDS) {
    explicitCount[f.key] = measurements.filter((e) => e[f.key] !== null && e[f.key] !== undefined).length;
  }

  // Direction per field based on user goal + sex (mirrors backend fitness_logic rules — display only).
  // Returns "reducir" | "aumentar" | "mantener".
  const directionFor = (fieldKey) => {
    const goal = profile?.goal;
    const sex = profile?.sex;
    if (!goal) return "mantener";
    if (fieldKey === "cintura_cm" || fieldKey === "abdomen_cm") {
      if (goal === "adelgazar") return "reducir";
      return "mantener";
    }
    if (fieldKey === "cadera_cm") {
      if (goal === "ganar_masa" && sex === "mujer") return "aumentar";
      if (goal === "adelgazar") return "reducir";
      return "mantener";
    }
    if (fieldKey === "gluteo_cm") {
      if (goal === "ganar_masa") return "aumentar";
      return "mantener";
    }
    if (fieldKey === "muslo_cm" || fieldKey === "cuadriceps_cm") {
      if (goal === "ganar_masa") return "aumentar";
      return "mantener";
    }
    // Male-specific
    if (fieldKey === "pecho_cm" || fieldKey === "brazo_cm" ||
        fieldKey === "pantorrilla_cm" || fieldKey === "hombros_cm") {
      if (goal === "ganar_masa" && sex === "hombre") return "aumentar";
      return "mantener";
    }
    return "mantener";
  };

  // "positivo" | "negativo" | "neutro" — valoración de un delta según objetivo del usuario.
  const assessDelta = (fieldKey, delta) => {
    if (delta === null || delta === undefined) return "neutro";
    const dir = directionFor(fieldKey);
    if (dir === "reducir") {
      if (delta < -0.1) return "positivo";
      if (delta > 0.1) return "negativo";
      return "neutro";
    }
    if (dir === "aumentar") {
      if (delta > 0.1) return "positivo";
      if (delta < -0.1) return "negativo";
      return "neutro";
    }
    // mantener: pequeña variación es neutra, grande es neutro-aviso (no castigamos)
    return "neutro";
  };

  // Timeseries for the currently selected measurement — ONLY entries that explicitly updated it.
  const singleFieldSeries = (() => {
    if (measurements.length === 0) return [];
    return measurements
      .filter((e) => e[chartField] !== null && e[chartField] !== undefined)
      .map((e) => ({
        date: new Date(e.recorded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }),
        shortDate: new Date(e.recorded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
        value: e[chartField],
      }));
  })();

  const hasAtLeastTwoMeasurements = measurements.length >= 2;
  const isFirstMeasurement = measurements.length === 1;

  return (
    <AppShell title="Mi progreso semanal" subtitle="Registra tu peso cada semana y tus medidas cuando quieras actualizarlas.">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mep-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl font-light tracking-tight">Gráfica de progreso</h3>
            <div className="text-eyebrow text-[var(--mep-pink)]">Tendencia</div>
          </div>
          {chartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-[var(--mep-text-muted)]">
              Aún no hay registros.
            </div>
          ) : (
            <div className="h-72" data-testid="progress-chart">
              <ResponsiveContainer>
                <LChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="pinkLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#E83D84" />
                      <stop offset="100%" stopColor="#D4AF37" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#A1A1AA" fontSize={12} />
                  <YAxis stroke="#A1A1AA" fontSize={12} domain={yDomain} />
                  {hasRange && (
                    <ReferenceArea
                      y1={rmin} y2={rmax}
                      fill="#D4AF37" fillOpacity={0.10}
                      stroke="#D4AF37" strokeOpacity={0.35}
                      strokeDasharray="4 4" ifOverflow="extendDomain"
                    >
                      <Label
                        value="Zona fitness recomendada"
                        position="insideTopLeft"
                        fill="#D4AF37" fontSize={11} offset={8}
                        style={{ letterSpacing: "0.15em", textTransform: "uppercase" }}
                      />
                    </ReferenceArea>
                  )}
                  {hasRange && (
                    <>
                      <ReferenceLine y={rmin} stroke="#D4AF37" strokeOpacity={0.5} strokeDasharray="2 4" />
                      <ReferenceLine y={rmax} stroke="#D4AF37" strokeOpacity={0.5} strokeDasharray="2 4" />
                    </>
                  )}
                  <Tooltip
                    contentStyle={{ background: "#121212", border: "1px solid #27272A", borderRadius: 8 }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(v) => [`${v} kg`, "Peso"]}
                  />
                  <Line type="monotone" dataKey="peso" stroke="url(#pinkLine)" strokeWidth={3} dot={{ fill: "#E83D84", r: 5 }} isAnimationActive={false} />
                </LChart>
              </ResponsiveContainer>
            </div>
          )}

          {zoneStatus && (
            <div
              data-testid={`progress-zone-${zoneStatus.kind}`}
              className={`mt-4 p-3 rounded-lg flex items-center gap-3 text-sm ${
                zoneStatus.kind === "in"
                  ? "bg-[rgba(212,175,55,0.10)] border-l-2 border-[var(--mep-gold)] text-[var(--mep-gold)]"
                  : "bg-[rgba(232,61,132,0.08)] border-l-2 border-[var(--mep-pink)] text-[var(--mep-text)]"
              }`}
            >
              <Target size={16} className={zoneStatus.kind === "in" ? "text-[var(--mep-gold)]" : "text-[var(--mep-pink)]"} />
              <span>{zoneStatus.text}</span>
            </div>
          )}

          <div className="mt-6 p-4 rounded-lg bg-[var(--mep-surface-2)] border-l-2 border-[var(--mep-gold)]">
            <div className="text-eyebrow mb-1">Mensaje motivacional</div>
            <p className="text-sm">{message}</p>
          </div>
        </div>

        <div className="mep-card p-6">
          <h3 className="font-display text-xl font-light tracking-tight mb-5">Registrar pesaje</h3>
          <p className="text-xs text-[var(--mep-text-muted)] mb-4">
            Solo peso. Tus medidas corporales se registran aparte.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Peso (kg)</label>
              <input
                data-testid="progress-weight-input"
                type="number" step="0.1" required value={val} onChange={(e) => setVal(e.target.value)}
                className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Nota (opcional)</label>
              <input
                data-testid="progress-note-input"
                type="text" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Ej. Semana 1"
                className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
              />
            </div>
            <button data-testid="progress-add-btn" disabled={loading} className="btn-pink w-full py-3 font-semibold flex items-center justify-center gap-2">
              <Plus size={16} /> {loading ? "Guardando..." : "Registrar"}
            </button>
          </form>
        </div>
      </div>

      {weights.length > 0 && (
        <div className="mt-8 mep-card p-6">
          <h3 className="font-display text-xl font-light tracking-tight mb-4">Historial de pesaje</h3>
          <div className="divide-y divide-[var(--mep-border)]">
            {[...weights].reverse().map((w) => (
              <div key={w.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{w.weight_kg} kg</div>
                  <div className="text-xs text-[var(--mep-text-muted)]">
                    {fmtDate(w.recorded_at)}
                    {w.note && <> · {w.note}</>}
                  </div>
                </div>
                <button data-testid={`progress-delete-${w.id}`} onClick={() => remove(w.id)} className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] transition">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ BODY MEASUREMENTS SECTION ============ */}
      <div id="medidas" className="mt-10 pt-6 border-t border-[var(--mep-border)]">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-3xl font-light tracking-tight flex items-center gap-3">
              <Ruler className="text-[var(--mep-gold)]" size={28} />
              Medidas corporales
            </h2>
            <p className="text-sm text-[var(--mep-text-muted)] mt-1">
              Opcionales. Solo cambian cuando tú las actualizas — no se duplican con tu pesaje.
            </p>
          </div>
          <Link
            to="/mi-plan"
            data-testid="go-to-my-plan"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--mep-pink)]/40 bg-[rgba(232,61,132,0.08)] text-sm text-[var(--mep-pink)] hover:bg-[rgba(232,61,132,0.15)] transition"
          >
            Ver mi plan personalizado <ChevronRight size={14} />
          </Link>
        </div>

        {dueForMeasurements && measurements.length > 0 && (
          <div
            data-testid="measurement-reminder-progress"
            className="mb-5 p-4 rounded-lg bg-[rgba(232,61,132,0.08)] border-l-2 border-[var(--mep-pink)] flex items-center gap-3 text-sm"
          >
            <Clock size={16} className="text-[var(--mep-pink)]" />
            <span>
              Han pasado <strong>{daysSince}</strong> días desde tu última medida. Recomendamos actualizar cada 30 días.
            </span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="mep-card p-6">
            <h3 className="font-display text-xl font-light tracking-tight mb-3">Registrar medidas</h3>
            <p className="text-xs text-[var(--mep-text-muted)] leading-relaxed mb-5" data-testid="measurement-form-note">
              Para comparar tu progreso correctamente, toma siempre las medidas en el mismo punto, de pie, con la cinta recta y sin apretarla.
            </p>
            <form onSubmit={submitMeasurements} className="space-y-3" data-testid="measurement-form">
              {visibleFields.map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-1 block">
                    {f.label} (cm)
                    {lastKnown[f.key] !== undefined && (
                      <span className="normal-case tracking-normal text-[10px] text-[var(--mep-text-muted)] ml-2">
                        última: {lastKnown[f.key]} cm · {fmtDate(lastDate[f.key])}
                      </span>
                    )}
                  </label>
                  <input
                    data-testid={`measurement-input-${f.key}`}
                    type="number" step="0.1" value={m[f.key]} onChange={(e) => setM({ ...m, [f.key]: e.target.value })}
                    placeholder={lastKnown[f.key] !== undefined ? String(lastKnown[f.key]) : "opcional"}
                    className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]"
                  />
                  <p className="text-[11px] text-[var(--mep-text-muted)] mt-1 leading-snug italic" data-testid={`measurement-hint-${f.key}`}>
                    {f.hint}
                  </p>
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-1 block">Nota (opcional)</label>
                <input
                  data-testid="measurement-note-input"
                  type="text" value={mNote} onChange={(e) => setMNote(e.target.value)}
                  placeholder="Ej. Mes 1"
                  className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]"
                />
              </div>
              <button data-testid="measurement-submit-btn" disabled={mLoading} className="btn-pink w-full py-3 font-semibold flex items-center justify-center gap-2">
                <Plus size={16} /> {mLoading ? "Guardando..." : "Registrar medidas"}
              </button>
            </form>
          </div>

          {/* Chart + Summary */}
          <div className="lg:col-span-2 mep-card p-6">
            {/* === CASE A: NO measurements === */}
            {measurements.length === 0 && (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display text-xl font-light tracking-tight">Evolución de medidas</h3>
                  <div className="text-eyebrow text-[var(--mep-gold)]">cm</div>
                </div>
                <div className="h-72 flex items-center justify-center text-[var(--mep-text-muted)] text-sm text-center px-4" data-testid="measurement-empty">
                  Aún no has registrado medidas corporales.<br />Al añadirlas verás tu evolución y recomendaciones personalizadas.
                </div>
              </>
            )}

            {/* === CASE B: FIRST measurement (no evolution yet) === */}
            {isFirstMeasurement && (
              <div data-testid="first-measurement-card">
                <div className="p-5 rounded-xl bg-[rgba(212,175,55,0.08)] border border-[var(--mep-gold)]/40">
                  <div className="flex items-center gap-3 mb-2">
                    <Ruler size={20} className="text-[var(--mep-gold)]" />
                    <div className="text-eyebrow text-[var(--mep-gold)]">Primera medición registrada</div>
                  </div>
                  <p className="text-sm text-[var(--mep-text-muted)] leading-relaxed">
                    Cuando actualices tus medidas nuevamente podrás comparar los cambios de tu cuerpo y ver tu evolución.
                  </p>
                </div>

                <div className="mt-5">
                  <div className="text-eyebrow mb-3">Tus medidas actuales · {fmtDate(measurements[0].recorded_at)}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {visibleFields.map((f) => {
                      const v = measurements[0][f.key];
                      if (v === null || v === undefined) return null;
                      return (
                        <div key={f.key} data-testid={`first-${f.key}`} className="p-3 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]">
                          <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">{f.label}</div>
                          <div className="font-display text-xl mt-1">{v} cm</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* === CASE C: 2+ measurements === */}
            {hasAtLeastTwoMeasurements && (
              <>
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <h3 className="font-display text-xl font-light tracking-tight">Evolución de medidas</h3>
                  <div className="text-eyebrow text-[var(--mep-gold)]">cm</div>
                </div>

                {/* Tabs selector */}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="chart-tabs">
                  {visibleFields.map((f) => {
                    const active = chartField === f.key;
                    const disabled = explicitCount[f.key] < 2;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setChartField(f.key)}
                        data-testid={`chart-tab-${f.key}`}
                        disabled={disabled}
                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] transition border ${
                          active
                            ? "bg-[var(--mep-pink)] text-white border-[var(--mep-pink)]"
                            : disabled
                              ? "bg-transparent text-[var(--mep-text-muted)] border-[var(--mep-border)] opacity-50 cursor-not-allowed"
                              : "bg-transparent text-[var(--mep-text)] border-[var(--mep-border)] hover:border-[var(--mep-pink)]/60"
                        }`}
                      >
                        {f.label}
                        {disabled && <span className="ml-1 normal-case tracking-normal text-[9px]">(1 dato)</span>}
                      </button>
                    );
                  })}
                </div>

                {singleFieldSeries.length < 2 ? (
                  <div className="h-56 flex items-center justify-center text-[var(--mep-text-muted)] text-sm text-center px-4" data-testid="chart-single-datapoint">
                    Solo hay una medición de <strong>{MEASUREMENT_FIELDS.find((x) => x.key === chartField)?.label}</strong>.<br />
                    Registra una nueva para ver la evolución.
                  </div>
                ) : (
                  <div className="h-72 w-full min-h-[280px]" data-testid={`measurement-chart-${chartField}`}>
                    <ResponsiveContainer>
                      <LChart data={singleFieldSeries} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                        <defs>
                          <linearGradient id="measureLine" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#E83D84" />
                            <stop offset="100%" stopColor="#D4AF37" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                        <XAxis dataKey="shortDate" stroke="#A1A1AA" fontSize={12} />
                        <YAxis stroke="#A1A1AA" fontSize={12} domain={["auto", "auto"]} />
                        <Tooltip
                          contentStyle={{ background: "#121212", border: "1px solid #27272A", borderRadius: 8 }}
                          labelStyle={{ color: "#fff" }}
                          formatter={(v) => [`${v} cm`, MEASUREMENT_FIELDS.find((x) => x.key === chartField)?.label]}
                        />
                        <Line type="monotone" dataKey="value" stroke="url(#measureLine)" strokeWidth={3} dot={{ fill: "#E83D84", r: 5 }} isAnimationActive={false} />
                      </LChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Comparativa (only visible with ≥2 real measurements) */}
                <div className="mt-6 pt-5 border-t border-[var(--mep-border)]" data-testid="comparativa-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={14} className="text-[var(--mep-pink)]" />
                    <div className="text-eyebrow text-[var(--mep-pink)]">Comparativa</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visibleFields.map((f) => {
                      // Need 2+ explicit entries for this specific field
                      if (explicitCount[f.key] < 2) return null;
                      const curr = lastKnown[f.key];
                      const prev = prevKnown[f.key];
                      const delta = +(curr - prev).toFixed(1);
                      const assessment = assessDelta(f.key, delta);
                      const colorByAssessment = {
                        positivo: "text-[var(--mep-gold)]",
                        negativo: "text-[var(--mep-pink)]",
                        neutro: "text-[var(--mep-text-muted)]",
                      }[assessment];
                      return (
                        <div
                          key={f.key}
                          data-testid={`comparativa-${f.key}`}
                          data-assessment={assessment}
                          className="p-4 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]"
                        >
                          <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-1">{f.label}</div>
                          <div className="flex items-baseline gap-2 text-sm">
                            <span className="text-[var(--mep-text-muted)]">{prev} cm</span>
                            <span className="text-[var(--mep-text-muted)]">→</span>
                            <span className="font-display text-xl text-[var(--mep-text)]">{curr} cm</span>
                          </div>
                          <div className={`text-sm mt-1 ${colorByAssessment}`}>
                            {delta > 0 ? "+" : ""}{delta} cm
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ===== HISTORIAL (visible if there is at least 1 entry) ===== */}
        {measurements.length > 0 && (
          <div className="mt-6 mep-card p-6" data-testid="measurement-history">
            <h3 className="font-display text-xl font-light tracking-tight mb-4">Historial de medidas</h3>
            <div className="space-y-3">
              {[...measurements].reverse().map((entry, idxReversed) => {
                // Build "previous entry" per field (the next one in the reversed list that has that field)
                const originalIdx = measurements.length - 1 - idxReversed;
                const parts = MEASUREMENT_FIELDS
                  .filter((f) => entry[f.key] !== null && entry[f.key] !== undefined)
                  .map((f) => ({ label: f.label, value: entry[f.key], key: f.key }));

                // Compute deltas vs the most recent previous explicit value for each field.
                const deltas = parts.map((p) => {
                  let prevVal = null;
                  for (let j = originalIdx - 1; j >= 0; j--) {
                    const v = measurements[j][p.key];
                    if (v !== null && v !== undefined) { prevVal = v; break; }
                  }
                  if (prevVal === null) return null;
                  const d = +(p.value - prevVal).toFixed(1);
                  const assessment = assessDelta(p.key, d);
                  return { ...p, delta: d, assessment };
                }).filter(Boolean);

                return (
                  <div
                    key={entry.id}
                    data-testid={`history-${entry.id}`}
                    className="p-4 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="text-eyebrow text-[var(--mep-gold)]">
                        {fmtDate(entry.recorded_at)}
                        {entry.note && <span className="normal-case tracking-normal text-[11px] text-[var(--mep-text-muted)] ml-2">· {entry.note}</span>}
                      </div>
                      <button
                        data-testid={`measurement-delete-${entry.id}`}
                        onClick={() => removeMeasurement(entry.id)}
                        className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] transition"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="text-sm leading-relaxed">
                      {parts.length === 0 ? (
                        <span className="text-[var(--mep-text-muted)]">Sin campos</span>
                      ) : (
                        parts.map((p, i) => (
                          <span key={p.key}>
                            <span className="text-[var(--mep-text-muted)]">{p.label}</span>{" "}
                            <span className="text-[var(--mep-text)] font-medium">{p.value}</span>
                            {i < parts.length - 1 && <span className="text-[var(--mep-text-muted)]"> · </span>}
                          </span>
                        ))
                      )}
                    </div>
                    {deltas.length > 0 && (
                      <div className="text-xs mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {deltas.map((d) => (
                          <span
                            key={d.key}
                            className={
                              d.assessment === "positivo"
                                ? "text-[var(--mep-gold)]"
                                : d.assessment === "negativo"
                                  ? "text-[var(--mep-pink)]"
                                  : "text-[var(--mep-text-muted)]"
                            }
                          >
                            {d.label}: {d.delta > 0 ? "+" : ""}{d.delta} cm
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
